import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Tab, Tabs, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { getFeeRequests, confirmFeeSent, confirmFeeSentBulk } from "../../api/deliveryCompanyService";
import { formatCurrency, formatDate } from "../Admin/constants";

const VIEW_TABS = [
  { value: "new", labelKey: "dcFees:tabNew" },
  { value: "sent", labelKey: "dcShared:sentAwaitingConfirmation" },
  { value: "history", labelKey: "dcShared:history" },
];

const SOURCE_LABEL_KEYS = {
  Store: "dcFees:sourceStore",
  Deliveries: "dcFees:sourceDeliveries",
};

export default function Fees() {
  const { t } = useTranslation(["dcFees", "dcShared"]);
  const [view, setView] = useState("new");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busySettlementId, setBusySettlementId] = useState(null);
  const [sendingAll, setSendingAll] = useState(false);

  function loadRows() {
    setLoading(true);
    getFeeRequests({ status: view === "history" ? "2" : "1" })
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("dcFees:loadError"))
      .finally(() => setLoading(false));
  }

  useEffect(loadRows, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const displayRows = useMemo(() => {
    if (view === "new") return rows.filter((r) => !r.companyConfirmedSentAt);
    if (view === "sent") return rows.filter((r) => !!r.companyConfirmedSentAt);
    return rows;
  }, [rows, view]);

  const totalAmount = useMemo(
    () => displayRows.reduce((sum, r) => sum + (r.amount || 0), 0),
    [displayRows]
  );

  async function handleConfirmSent(row) {
    setBusySettlementId(row.commissionSettlementId);
    try {
      await confirmFeeSent(row.commissionSettlementId);
      loadRows();
    } catch {
      setError("dcFees:confirmError");
    } finally {
      setBusySettlementId(null);
    }
  }

  async function handleSendAll() {
    const ids = displayRows.map((r) => r.commissionSettlementId);
    if (ids.length === 0) return;

    setSendingAll(true);
    try {
      await confirmFeeSentBulk(ids);
      loadRows();
    } catch {
      setError("dcFees:sendAllError");
    } finally {
      setSendingAll(false);
    }
  }

  const columns = useMemo(() => {
    const base = [
      {
        field: "source",
        headerName: t("dcShared:from"),
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={SOURCE_LABEL_KEYS[params.value] ? t(SOURCE_LABEL_KEYS[params.value]) : params.value}
            color={params.value === "Store" ? "primary" : "secondary"}
          />
        ),
      },
      { field: "label", headerName: t("dcFees:storeSource"), flex: 1, minWidth: 180 },
      { field: "periodStart", headerName: t("dcShared:from"), width: 150, valueFormatter: (v) => formatDate(v) },
      { field: "periodEnd", headerName: t("dcShared:to"), width: 150, valueFormatter: (v) => formatDate(v) },
      { field: "orderCount", headerName: t("dcShared:orders"), width: 100 },
      { field: "amount", headerName: t("dcShared:amount"), width: 140, valueFormatter: (v) => formatCurrency(v) },
    ];

    if (view === "new") {
      return [
        ...base,
        { field: "generatedAt", headerName: t("dcFees:requestedAt"), width: 170, valueFormatter: (v) => formatDate(v) },
        {
          field: "sent",
          headerName: "",
          width: 150,
          sortable: false,
          renderCell: (params) => (
            <Button
              size="small"
              variant="contained"
              disabled={busySettlementId === params.row.commissionSettlementId}
              onClick={() => handleConfirmSent(params.row)}
            >
              {t("dcFees:iSentIt")}
            </Button>
          ),
        },
      ];
    }

    if (view === "sent") {
      return [
        ...base,
        {
          field: "companyConfirmedSentAt",
          headerName: t("dcShared:sentAt"),
          width: 170,
          valueFormatter: (v) => formatDate(v),
        },
        {
          field: "status",
          headerName: "",
          width: 200,
          sortable: false,
          renderCell: () => <Chip size="small" color="success" label={t("dcShared:sentAwaitingConfirmation")} />,
        },
      ];
    }

    return [...base, { field: "paidAt", headerName: t("dcFees:receivedAt"), width: 170, valueFormatter: (v) => formatDate(v) }];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, busySettlementId, t]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        {t("dcFees:title")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t("dcFees:description")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t(error)}
        </Alert>
      )}

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Tabs value={view} onChange={(e, v) => setView(v)}>
          {VIEW_TABS.map((tab) => (
            <Tab key={tab.value} value={tab.value} label={t(tab.labelKey)} />
          ))}
        </Tabs>

        {view === "new" && (
          <Button
            variant="contained"
            disabled={sendingAll || displayRows.length === 0}
            onClick={handleSendAll}
          >
            {t("dcFees:iSentAll", { count: displayRows.length })}
          </Button>
        )}
      </Box>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={displayRows}
          getRowId={(row) => row.commissionSettlementId}
          columns={columns}
          loading={loading}
          hideFooter
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
        {(view === "new" || view === "sent") && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 2,
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography variant="subtitle2">
              {t("dcShared:totalWithAmount", { amount: formatCurrency(totalAmount) })}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
